import { useCallback, useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import api from "@/api";
import { useI18n } from "@/i18n";

// A redeploy kills a running job mid-flight (3 occurrences, the last one cost a full day
// of API-Football quota). The banner is the human guard-rail; /admin/deploy-guard is the
// machine one.
export function useDeployGuard(intervalMs = 15000) {
  const [guard, setGuard] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/deploy-guard");
      setGuard(r.data);
    } catch {
      setGuard(null);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, intervalMs);
    return () => clearInterval(id);
  }, [load, intervalMs]);

  return guard;
}

export default function DeployGuardBanner() {
  const { t } = useI18n();
  const guard = useDeployGuard();
  if (!guard || guard.safe) return null;

  return (
    <div
      className="sticky top-0 z-50 mb-4 rounded-xl border border-red-500/60 bg-red-950/80 p-3 backdrop-blur-md"
      data-testid="deploy-guard-banner"
    >
      <p className="flex items-center gap-2 font-mono2 text-[11px] font-bold uppercase tracking-wider text-red-300">
        <AlertTriangle className="h-4 w-4 shrink-0" /> {t("admin.deploy_guard_title")}
      </p>
      <ul className="mt-1 font-mono2 text-[10px] text-slate-200">
        {guard.running.map((r) => (
          <li key={`${r.job}-${r.started_at}`} data-testid={`deploy-guard-job-${r.job}`}>
            {r.job} · {t("admin.deploy_guard_since", { min: Math.round((r.elapsed_s || 0) / 60) })} ·{" "}
            {t("admin.deploy_guard_requests", { n: r.requests })}
            {r.progress ? ` · ${r.progress}` : ""}
          </li>
        ))}
      </ul>
    </div>
  );
}
